import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { auth, db, firebase } from '../../firebase';
import { AuthContext, SIGN_OUT_FAILED, signOutFailureMessage } from '../../Providers/UserProvider';
import { rejectionMessage } from '../../utils/rejectionMessage';
import { getFormattedPhone, getIsValidPhone } from '../../utils/validatePhone';
import skillsList from '../../Constants/skillsList';

function writeAlertMessage(error: unknown, fallback: string): string {
    const detail = rejectionMessage(error, '');
    return detail ? `${fallback} ${detail}` : fallback;
}

function sameSkills(left: readonly string[] | null | undefined, right: readonly string[] | null | undefined): boolean {
    const a = left ?? [];
    const b = right ?? [];
    return a.length === b.length && a.every((skill, index) => skill === b[index]);
}

type QueuedSkillWrite = {
    skill: string;
    checked: boolean;
    /** Skill membership before this burst of clicks. A failed last click reverts here. */
    baselineIncluded: boolean;
};

const SettingsPage = () => {
    const { tech, user }: any = useContext(AuthContext);
    const [techRegion, setTechRegion] = useState('');
    const [regionError, setRegionError] = useState('');
    const [newPhone, setNewPhone] = useState<string>(tech.phone);
    const phoneDirtyRef = useRef(false);
    const [phoneError, setPhoneError] = useState('');
    /** Synchronous in-flight lock. setState disables Update too late to ignore a second click. */
    const phoneWriteLock = useRef(false);
    const [phoneWriteBusy, setPhoneWriteBusy] = useState(false);
    const [skills, setSkills] = useState<string[]>(() => tech.skills ?? []);
    const skillsRef = useRef<string[]>(tech.skills ?? []);
    const serverSkillsRef = useRef<string[]>(tech.skills ?? []);
    const skillsPendingRef = useRef(false);
    /** Ignores stale UI completions. Commit order is the promise chain below, not this counter. */
    const skillsWriteSeq = useRef(0);
    const skillsWriteTailRef = useRef<Promise<void>>(Promise.resolve());
    const skillsWriteQueueRef = useRef<QueuedSkillWrite[]>([]);
    const skillsInFlightRef = useRef<{ skill: string; baselineIncluded: boolean } | null>(null);
    const [skillsError, setSkillsError] = useState('');
    const [logoutError, setLogoutError] = useState('');
    serverSkillsRef.current = tech.skills ?? [];

    useEffect(() => {
        if (phoneDirtyRef.current) {
            return;
        }
        setNewPhone(tech.phone);
    }, [tech.phone]);

    useEffect(() => {
        const serverSkills = tech.skills ?? [];
        if (skillsPendingRef.current && !sameSkills(serverSkills, skillsRef.current)) {
            return;
        }
        skillsPendingRef.current = false;
        const next = [...serverSkills];
        skillsRef.current = next;
        setSkills(next);
    }, [tech.skills]);

    const isValidPhone = useMemo(() => getIsValidPhone(newPhone), [newPhone]);

    const isPhoneChanged = useMemo(() => {
        if (!isValidPhone) {
            return true;
        }
        const formattedStored = getFormattedPhone(tech.phone);
        if (formattedStored == null) {
            return true;
        }
        return getFormattedPhone(newPhone) !== formattedStored;
    }, [newPhone, tech.phone, isValidPhone]);

    const formatNewPhone = () => {
        if (!isValidPhone) {
            return;
        }
        const formatted = getFormattedPhone(newPhone);
        if (formatted == null) {
            return;
        }
        setNewPhone(formatted);
    };

    const regionId = useMemo(() => (user ? user?.claims?.regionId : null), [user]);

    const updatePhone = async () => {
        if (phoneWriteLock.current) return;
        phoneWriteLock.current = true;
        setPhoneWriteBusy(true);
        try {
            await db
                .collection(`/regions/${user.claims.regionId}/techs`)
                .doc(user.uid)
                .update({ phone: newPhone?.toString() });
            phoneDirtyRef.current = false;
            setPhoneError('');
        } catch (error) {
            console.error('Phone number could not be updated.', error);
            setPhoneError(writeAlertMessage(error, 'Phone number could not be updated.'));
        } finally {
            phoneWriteLock.current = false;
            setPhoneWriteBusy(false);
        }
    };

    const updateSkills = (skill: string, checked: boolean) => {
        const writeId = ++skillsWriteSeq.current;
        // One skill per write. arrayUnion adds it; arrayRemove drops it.
        const skillsValue = checked
            ? firebase.firestore.FieldValue.arrayUnion(skill)
            : firebase.firestore.FieldValue.arrayRemove(skill);
        const queuedSameSkill = skillsWriteQueueRef.current.find((item) => item.skill === skill);
        const inFlight = skillsInFlightRef.current;
        let baselineIncluded = !checked;
        if (queuedSameSkill) {
            baselineIncluded = queuedSameSkill.baselineIncluded;
        } else if (inFlight && inFlight.skill === skill) {
            baselineIncluded = inFlight.baselineIncluded;
        }
        skillsWriteQueueRef.current.push({ skill, checked, baselineIncluded });

        // One update at a time for this tech, same as vacation days. If a later click
        // for this skill is already queued, skip this write so the last click commits last.
        skillsWriteTailRef.current = skillsWriteTailRef.current
            .catch(() => undefined)
            .then(async () => {
                const current = skillsWriteQueueRef.current.shift();
                if (!current) {
                    return;
                }
                if (skillsWriteQueueRef.current.some((item) => item.skill === skill)) {
                    return;
                }
                // Read baseline at commit time. An earlier write in this chain may have
                // landed and updated it after this click was queued.
                const baselineAtCommit = current.baselineIncluded;
                skillsInFlightRef.current = { skill, baselineIncluded: baselineAtCommit };
                try {
                    await db.doc(`/regions/${regionId}/techs/${tech.id}`).update({ skills: skillsValue });
                    skillsWriteQueueRef.current = skillsWriteQueueRef.current.map((item) =>
                        item.skill === skill ? { ...item, baselineIncluded: checked } : item,
                    );
                    if (writeId !== skillsWriteSeq.current) {
                        return;
                    }
                    setSkillsError('');
                    skillsPendingRef.current = false;
                    if (sameSkills(serverSkillsRef.current, skillsRef.current)) {
                        const next = [...(serverSkillsRef.current ?? [])];
                        skillsRef.current = next;
                        setSkills(next);
                    }
                } catch (error) {
                    if (writeId !== skillsWriteSeq.current) {
                        return;
                    }
                    console.error('Skills could not be updated.', error);
                    setSkillsError(writeAlertMessage(error, 'Skills could not be updated.'));
                    skillsPendingRef.current = false;
                    const withoutSkill = (skillsRef.current ?? []).filter((entry) => entry !== skill);
                    const reverted = baselineAtCommit ? withoutSkill.concat(skill) : withoutSkill;
                    skillsRef.current = reverted;
                    setSkills(reverted);
                } finally {
                    skillsInFlightRef.current = null;
                }
            });
    };

    useEffect(
        () =>
            db
                .collection('/regions')
                .doc(regionId)
                .onSnapshot(
                    (regionDoc) => {
                        const data = regionDoc.data();
                        setTechRegion(data?.name);
                        setRegionError('');
                    },
                    (error) => {
                        console.error('Unable to load region', error);
                        setTechRegion('');
                        setRegionError(writeAlertMessage(error, 'Region name could not load.'));
                    },
                ),
        [regionId],
    );

    const onChangeHandler = (setFunc: (value: any) => void) => (evt) => setFunc(evt.currentTarget.value);

    const logout = async () => {
        try {
            await auth.signOut();
            setLogoutError('');
        } catch (error) {
            console.error(SIGN_OUT_FAILED, error);
            setLogoutError(signOutFailureMessage(error));
        }
    };

    return (
        <div className="overflow-y-scroll mb-16">
            <div className="bg-white-600 py-4 text-center shadow-lg">
                <div className="my-auto">
                    <p className="text-2xl text-gray-800 font-semibold">Settings</p>
                    <p className="text-lg text-gray-800">Welcome {tech.name}</p>
                </div>
            </div>
            <div className="flex flex-col p-8">
                <h2 className="text-2xl underline"> Account details </h2>
                <div className="mt-4 text-base text-gray-600 text-left">
                    <p className="my-1">Name: {tech.name}</p>
                    <p className="my-1">Email: {tech.email}</p>
                    <p className="my-1">Phone: {tech.phone}</p>
                </div>
                <div className="mt-4">
                    <p className="mr-3 text-gray-800 mb-3">You are currently set to: {techRegion}</p>
                    {regionError ? (
                        <p className="text-red-600 text-sm mb-3" role="alert" data-testid="settings-region-error">
                            {regionError}
                        </p>
                    ) : null}
                    {/* prettier-ignore */}
                    <p
                        id="regionSelect"
                        className="text-sm text-gray-500"
                        data-testid="settings-region-read-only"
                    >
                        Change region is read-only here — Settings does not write a region claim.
                        Mid-DEMO WALK switch is on /app/orders.
                    </p>
                </div>
                <div className="mt-4">
                    <p className="text-gray-800 ">Change Phone Number</p>
                    <div className="flex">
                        <input
                            id="changePhone"
                            type="text"
                            value={newPhone ?? ''}
                            onBlur={formatNewPhone}
                            className="block border border-gray-400 rounded text-right focus:outline-none focus:ring-2 focus:ring-gray-200 mr-3 px-2"
                            onChange={(evt) => {
                                phoneDirtyRef.current = true;
                                onChangeHandler(setNewPhone)(evt);
                            }}
                        />
                        <button
                            type="button"
                            className={`text-white py-2 px-4  rounded ${
                                phoneWriteLock.current || phoneWriteBusy || !isValidPhone || !isPhoneChanged
                                    ? 'opacity-50 bg-gray-400 cursor-auto'
                                    : 'bg-red-400 hover:bg-red-700'
                            }`}
                            disabled={phoneWriteLock.current || phoneWriteBusy || !isValidPhone || !isPhoneChanged}
                            onClick={updatePhone}
                        >
                            Update
                        </button>
                    </div>
                    {!isValidPhone && <small className="text-red-500">Phone number is invalid</small>}
                    {phoneError ? (
                        <p className="text-red-600 text-sm mt-4" role="alert" data-testid="settings-phone-error">
                            {phoneError}
                        </p>
                    ) : null}
                </div>
            </div>

            <div className="flex flex-col p-8">
                <h2 className="text-2xl underline mb-2"> Your skills </h2>
                {skillsList.map((skill) => (
                    <div key={skill} className="inline">
                        <span>
                            {skill}
                            <input
                                checked={skills.includes(skill)}
                                onChange={(evt) => {
                                    const checked = evt.target.checked;
                                    const next = (skillsRef.current ?? [])
                                        .filter((entry) => entry !== skill)
                                        .concat(checked ? [skill] : []);
                                    skillsPendingRef.current = true;
                                    skillsRef.current = next;
                                    setSkills(next);
                                    void updateSkills(skill, checked);
                                }}
                                className="ml-3"
                                type="checkbox"
                            />
                        </span>
                    </div>
                ))}
                {skillsError ? (
                    <p className="text-red-600 text-sm mt-4" role="alert" data-testid="settings-skills-error">
                        {skillsError}
                    </p>
                ) : null}
            </div>

            <div className="flex flex-col p-8">
                <h2 className="text-2xl underline"> Logout </h2>

                <div className="mt-4 text-base text-gray-600 text-left">
                    <p className="my-1">
                        SolutionWorks is a resource management app designed for you. If you have any questions or issues
                        please email us at:{' '}
                        <a className="text-blue-400" href="mailto:example@solutionworks.com">
                            example@solutionworks.com
                        </a>
                    </p>
                </div>
                <div className="text-center mt-12">
                    <button
                        type="button"
                        className="bg-primaryTheme bg-blue-700 hover:bg-blue-900 text-white font-bold py-2 px-4 rounded w-72"
                        onClick={() => {
                            void logout();
                        }}
                    >
                        Logout
                    </button>
                    {logoutError ? (
                        <p className="text-red-600 text-sm mt-4" role="alert" data-testid="settings-logout-error">
                            {logoutError}
                        </p>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
export default SettingsPage;
